import React from 'react';
import { cn } from '../../utils/cn';

export default function Select({
	id,
	value,
	onChange,
	placeholder = 'Select an option',
	options = [],
	disabled = false,
	loading = false,
	className = '',
	onFocus,
	required = false,
	style
}) {
	const [open, setOpen] = React.useState(false);
	const triggerRef = React.useRef(null);
	const menuRef = React.useRef(null);
	const [highlightIndex, setHighlightIndex] = React.useState(-1);

	const currentOption = options.find((o) => o.value === value);
	const displayLabel = currentOption?.label || placeholder;

	React.useEffect(() => {
		function onClickOutside(e) {
			if (!open) return;
			if (menuRef.current && !menuRef.current.contains(e.target) && triggerRef.current && !triggerRef.current.contains(e.target)) {
				setOpen(false);
			}
		}
		document.addEventListener('mousedown', onClickOutside);
		document.addEventListener('touchstart', onClickOutside);
		return () => {
			document.removeEventListener('mousedown', onClickOutside);
			document.removeEventListener('touchstart', onClickOutside);
		};
	}, [open]);

	const handleSelect = (newValue) => {
		if (disabled || loading) return;
		const evt = { target: { value: newValue } };
		onChange && onChange(evt);
		setOpen(false);
	};

	const onKeyDown = (e) => {
		if (disabled || loading) return;
		switch (e.key) {
			case 'Enter':
				if (!open) {
					setOpen(true);
					break;
				}
				if (highlightIndex >= 0 && options[highlightIndex] && !options[highlightIndex].disabled) {
					handleSelect(options[highlightIndex].value);
				}
				break;
			case 'ArrowDown':
				e.preventDefault();
				setOpen(true);
				setHighlightIndex((i) => {
					const next = Math.min((i < 0 ? -1 : i) + 1, options.length - 1);
					return next;
				});
				break;
			case 'ArrowUp':
				e.preventDefault();
				setOpen(true);
				setHighlightIndex((i) => Math.max((i < 0 ? options.length : i) - 1, 0));
				break;
			case 'Escape':
				setOpen(false);
				break;
			default:
		}
	};

	const triggerClasses = cn(
		'w-full rounded-[15px] px-4 xl:py-3 py-2 text-base',
		'border-1p3 border-lightGrey bg-white',
		'flex items-center justify-between text-dark-blue',
		'appearance-none focus:outline-none focus:ring-0 transition-colors',
		'hover:border-[#D9DCE1] focus:border-purple',
		disabled ? 'bg-bgColor text-gray cursor-not-allowed' : '',
		className
	);

	const caretStyle = loading
		? { backgroundImage: 'none' }
		: {
				backgroundImage:
					"url(\"data:image/svg+xml;utf8,<svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M14.5 7.5L10 13.5L5.5 7.5' stroke='%23180A63' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' opacity='0.5'/></svg>\")",
				backgroundRepeat: 'no-repeat',
				backgroundSize: '20px 20px',
				backgroundPosition: 'right 8px center',
				paddingRight: '28px'
		  };

	return (
		<div className="relative" style={style}>
			<button
				type="button"
				id={id}
				ref={triggerRef}
				onClick={() => {
					if (disabled || loading) return;
					setOpen((o) => !o);
					if (onFocus) onFocus();
				}}
				onKeyDown={onKeyDown}
				className={triggerClasses}
				aria-haspopup="listbox"
				aria-expanded={open}
				disabled={disabled}
				style={caretStyle}
			>
				<span className={cn('truncate', !currentOption ? 'text-gray' : 'text-dark-blue')}>
					{displayLabel}
				</span>
				{loading && (
					<span className="pointer-events-none absolute right-[8px] top-1/2 -translate-y-1/2">
						<svg className="h-5 w-5 animate-spin text-dark-blue " viewBox="0 0 24 24" fill="none">
							<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
							<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
						</svg>
					</span>
				)}
			</button>

			{open && (
				<div
					ref={menuRef}
					className="absolute z-50 mt-2 w-full bg-white border border-lightGrey rounded-[15px] overflow-hidden"
					style={{ boxShadow: '2px 2px 20px rgba(0, 0, 0, 0.10)' }}
					role="listbox"
					aria-labelledby={id}
				>
					{/* Optional divider/top border spacing */}
					<div className="h-px w-full bg-[#F0F0F0]" />
					<ul className="max-h-64 overflow-auto py-1">
						{options.map((opt, idx) => {
							const isActive = value === opt.value;
							const isDisabled = Boolean(opt.disabled);
							return (
								<li
									key={opt.value}
									role="option"
									aria-selected={isActive}
									onMouseEnter={() => setHighlightIndex(idx)}
									onClick={() => !isDisabled && handleSelect(opt.value)}
									className={cn(
										'px-4 py-2 text-sm cursor-pointer flex items-center justify-between',
										isDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-dark-blue hover:bg-[#F8F8FA]',
										isActive ? 'bg-[#F5F0FF] font-medium' : '',
										highlightIndex === idx && !isDisabled ? 'bg-[#F8F8FA]' : ''
									)}
								>
									<span className="truncate">{opt.label}</span>
									{isActive && (
										<svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path d="M5.99992 9.2002L1.79992 5.0002L0.399902 6.4002L5.99992 12.0002L15.9999 2.0002L14.5999 0.600195L5.99992 9.2002Z" fill="#180A63"/>
										</svg>
									)}
								</li>
							);
						})}
					</ul>
				</div>
			)}
		</div>
	);
}
